import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle, XCircle, Trash2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { toast } from "@/components/ui/Toast";
import { ReviewerControls, type ReviewCommentData } from "@/components/assessments/ReviewerControls";
import { ApproveDialog } from "@/components/assessments/ApproveDialog";
import { RejectDialog } from "@/components/assessments/RejectDialog";
import { useAuthStore } from "@/store/auth.store";
import { userCan } from "@/lib/permissions";
import { useTranslation } from "@/hooks/useTranslation";
import type { AssessmentStatus } from "@/types";
import type { QuestionType, QuestionOptions } from "@/components/assessments/QuestionCard";

// ─────────────────────────────────────────────
// AssessmentReview
//
// Screen 14 — /assessments/:id/review
// Read-only questions with per-question reviewer controls.
// Design: §8.5 Screen 14: Assessment Review
// ─────────────────────────────────────────────

interface ResponseRecord {
  id: string;
  questionId: string;
  value: unknown;
  comment: string | null;
}

interface QuestionRecord {
  id: string;
  code: string;
  text: string;
  helpText?: string;
  type: QuestionType;
  options: QuestionOptions | null;
  sortOrder: number;
}

interface PostureRecord {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  questions: QuestionRecord[];
}

interface DomainRecord {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  postures: PostureRecord[];
}

interface ReviewProgressItem {
  domainId: string;
  domainCode: string;
  domainName: string;
  totalQuestions: number;
  reviewed: number;
  needsRevision: number;
  notStarted: number;
}

interface AssessmentReviewResponse {
  data: {
    id: string;
    entityId: string;
    status: AssessmentStatus;
    currentVersion: number;
    entity?: { id: string; name: string };
    schemaVersion?: {
      version: number;
      schema?: { name: string };
      domains?: DomainRecord[];
    };
    responses?: ResponseRecord[];
    reviewComments?: ReviewCommentData[];
    responseSummary?: { total: number; answered: number; completionPct: number };
  };
}

function renderValue(
  type: QuestionType,
  value: unknown,
  _options: QuestionOptions | null,
  t: (key: string) => string
): string {
  if (value === null || value === undefined) return t("assessment.review.notAnsweredValue");

  if (type === "WEIGHTED_YES_NO" || type === "WEIGHTED_MULTIPLE_CHOICE") {
    return String(value);
  }

  if (type === "MULTI_SUB_FIELD") {
    const v = value as Record<string, string>;
    return Object.entries(v)
      .filter(([, val]) => val)
      .map(([k, val]) => `${k}: ${val}`)
      .join(", ");
  }

  return String(value);
}

export function AssessmentReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const canDelete = userCan(user, "delete", "Assessment");

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeDomainId, setActiveDomainId] = useState<string | null>(null);
  const [reviewMap, setReviewMap] = useState<Record<string, ReviewCommentData>>({});

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/assessments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      toast.success(t("assessment.review.deleted"));
      navigate("/assessments");
    },
    onError: (err: Error) => {
      setDeleteOpen(false);
      toast.error(err.message ?? t("assessment.review.deleteFailed"));
    },
  });

  const { data, isLoading, error } = useQuery<AssessmentReviewResponse>({
    queryKey: ["assessment-review", id],
    queryFn: () =>
      api.get(`/assessments/${id}?include=domains,responses,review-comments`),
    enabled: !!id,
  });

  const assessment = data?.data;

  // Build response map: questionId -> { id, value, comment }
  const responseMap = useMemo(() => {
    const map: Record<string, { id: string; value: unknown; comment: string | null }> = {};
    for (const r of assessment?.responses ?? []) {
      map[r.questionId] = { id: r.id, value: r.value, comment: r.comment };
    }
    return map;
  }, [assessment?.responses]);

  // Build review comment map keyed by questionId (from API + local changes)
  const mergedReviewMap = useMemo(() => {
    const base: Record<string, ReviewCommentData> = {};
    for (const rc of assessment?.reviewComments ?? []) {
      if (rc.questionId) base[rc.questionId] = rc;
    }
    // Local optimistic updates (keyed by questionId)
    return { ...base, ...reviewMap };
  }, [assessment?.reviewComments, reviewMap]);

  // Start review mutation (SUBMITTED -> IN_REVIEW)
  const startReviewMutation = useMutation({
    mutationFn: () => api.post(`/assessments/${id}/start-review`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment-review", id] });
      queryClient.invalidateQueries({ queryKey: ["assessment", id] });
      toast.success(t("assessment.review.reviewStarted"));
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.failedToStartReview")),
  });

  if (isLoading) return <PageSkeleton />;

  if (error || !assessment) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-[16px] text-text-primary">{t("assessment.notFound")}</p>
        <Button variant="ghost" onClick={() => navigate("/assessments")} className="mt-4">
          {t("assessment.backToAssessments")}
        </Button>
      </div>
    );
  }

  const status = assessment.status;
  const domains = [...(assessment.schemaVersion?.domains ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  // Set initial active domain
  const activeDomain =
    (activeDomainId ? domains.find((d) => d.id === activeDomainId) : null) ??
    domains[0] ??
    null;

  // Compute review progress per domain — scoped to questions in that domain
  const reviewProgress: ReviewProgressItem[] = domains.map((domain) => {
    const domainQuestionIds = new Set(
      domain.postures.flatMap((p) => p.questions.map((q) => q.id))
    );
    const totalQuestions = domainQuestionIds.size;
    const reviewed = Object.entries(mergedReviewMap).filter(
      ([qId, rc]) => domainQuestionIds.has(qId) && rc.status === "REVIEWED"
    ).length;
    const needsRevision = Object.entries(mergedReviewMap).filter(
      ([qId, rc]) => domainQuestionIds.has(qId) && rc.status === "NEEDS_REVISION"
    ).length;
    return {
      domainId: domain.id,
      domainCode: domain.code,
      domainName: domain.name,
      totalQuestions,
      reviewed,
      needsRevision,
      notStarted: totalQuestions - reviewed - needsRevision,
    };
  });

  const totalReviewed = reviewProgress.reduce((sum, r) => sum + r.reviewed, 0);
  const totalQuestions = reviewProgress.reduce((sum, r) => sum + r.totalQuestions, 0);
  const reviewPct = totalQuestions > 0 ? Math.round((totalReviewed / totalQuestions) * 100) : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate(`/assessments/${id}`)}
            className="mt-1 flex h-7 w-7 items-center justify-center rounded-control text-text-muted hover:bg-hover hover:text-text-secondary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[24px] font-semibold text-text-primary">
                {assessment.entity?.name ?? t("assessment.fallbackName")}
              </h1>
              <StatusBadge type="assessment" status={status} />
              {assessment.schemaVersion?.schema?.name && (
                <span
                  className="rounded-badge px-2 py-0.5 text-[12px] font-semibold"
                  style={{
                    backgroundColor: "var(--accent-bg)",
                    color: "var(--accent-text)",
                  }}
                >
                  {assessment.schemaVersion.schema.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-32 overflow-hidden rounded-full" style={{ backgroundColor: "var(--bg-hover)" }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${reviewPct}%`,
                    backgroundColor: "var(--accent)",
                  }}
                />
              </div>
              <span className="text-[12px] text-text-muted">
                {t("assessment.review.reviewedCount", { reviewed: totalReviewed, total: totalQuestions })}
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {status === "SUBMITTED" && (
            <Button
              size="sm"
              onClick={() => startReviewMutation.mutate()}
              isLoading={startReviewMutation.isPending}
            >
              {t("assessment.startReview")}
            </Button>
          )}
          {status === "IN_REVIEW" && (
            <>
              <Button
                variant="outline"
                size="sm"
                style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
                onClick={() => setRejectOpen(true)}
                leadingIcon={<XCircle className="h-4 w-4" />}
              >
                {t("common.reject")}
              </Button>
              <Button
                size="sm"
                style={{ backgroundColor: "var(--success)" }}
                onClick={() => setApproveOpen(true)}
                leadingIcon={<CheckCircle className="h-4 w-4" />}
              >
                {t("assessment.approveDialog.title")}
              </Button>
            </>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
              onClick={() => setDeleteOpen(true)}
              leadingIcon={<Trash2 className="h-4 w-4" />}
            >
              {t("common.delete")}
            </Button>
          )}
        </div>
      </div>

      {/* Content: sidebar + questions */}
      <div className="flex gap-5 items-start">
        {/* Domain/Posture navigation sidebar */}
        <div
          className="flex-shrink-0 w-60 rounded-card border border-border-default bg-card overflow-hidden sticky top-4"
          style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto" }}
        >
          <div className="p-3 border-b border-border-subtle">
            <p className="text-[11px] font-semibold uppercase tracking-[1.5px] text-text-muted">
              {t("assessment.review.sections")}
            </p>
          </div>
          <div className="flex flex-col py-1">
            {reviewProgress.map((rp) => (
              <button
                key={rp.domainId}
                type="button"
                onClick={() => setActiveDomainId(rp.domainId)}
                className="flex items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-hover"
                style={{
                  backgroundColor:
                    activeDomain?.id === rp.domainId ? "var(--accent-bg)" : undefined,
                }}
              >
                <span
                  className="text-[13px] font-medium truncate"
                  style={{
                    color:
                      activeDomain?.id === rp.domainId
                        ? "var(--accent-text)"
                        : "var(--text-secondary)",
                  }}
                >
                  {rp.domainName}
                </span>
                <span
                  className="rounded-badge px-1.5 py-0.5 text-[11px] font-semibold flex-shrink-0"
                  style={{
                    backgroundColor:
                      rp.reviewed > 0 ? "var(--success-bg)" : "var(--bg-hover)",
                    color: rp.reviewed > 0 ? "var(--success)" : "var(--text-muted)",
                  }}
                >
                  {rp.reviewed}/{rp.totalQuestions}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Questions area */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {activeDomain ? (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-[18px] font-semibold text-text-primary">
                  {activeDomain.name}
                </h2>
              </div>

              {[...activeDomain.postures]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((posture) => (
                  <div key={posture.id} className="flex flex-col gap-0">
                    {/* Posture header */}
                    <div className="flex items-center justify-between gap-4 rounded-t-card border border-b-0 border-border-default bg-hover px-5 py-3">
                      <span className="text-[14px] font-semibold text-text-primary">
                        {posture.name}
                      </span>
                    </div>

                    {/* Questions */}
                    <div className="rounded-b-card border border-border-default overflow-hidden">
                      <div className="flex flex-col divide-y divide-border-subtle">
                        {[...posture.questions]
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((question, qi) => {
                            const response = responseMap[question.id];
                            // Look up existing review by questionId (mergedReviewMap is keyed by questionId)
                            const existingReview = mergedReviewMap[question.id] ?? null;

                            return (
                              <div key={question.id} className="bg-card p-5">
                                {/* Question */}
                                <div className="flex items-start gap-3 mb-3">
                                  <span
                                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
                                    style={{
                                      backgroundColor: "var(--bg-hover)",
                                      color: "var(--text-muted)",
                                    }}
                                  >
                                    {qi + 1}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="text-[14px] font-medium text-text-primary">
                                      {question.text}
                                    </p>
                                    {question.helpText && (
                                      <p className="mt-0.5 text-[13px] text-text-muted">
                                        {question.helpText}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Response */}
                                <div className="ml-9 mb-3">
                                  <div
                                    className="rounded-control px-3 py-2"
                                    style={{ backgroundColor: "var(--bg-hover)" }}
                                  >
                                    {response ? (
                                      <>
                                        <p className="text-[14px] text-text-secondary">
                                          {renderValue(question.type, response.value, question.options, t)}
                                        </p>
                                        {response.comment && (
                                          <p className="mt-1 text-[13px] text-text-muted italic">
                                            {t("assessment.review.pmNote", { comment: response.comment })}
                                          </p>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-[13px] text-text-muted italic">
                                        {t("assessment.review.notAnswered")}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Reviewer controls */}
                                <div className="ml-9">
                                  <ReviewerControls
                                    assessmentId={id!}
                                    questionId={question.id}
                                    responseId={response?.id ?? null}
                                    existingReview={existingReview}
                                    onReviewSaved={(review) => {
                                      setReviewMap((prev) => ({
                                        ...prev,
                                        // Key by questionId so lookup stays consistent
                                        [question.id]: { ...review, questionId: question.id },
                                      }));
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                ))}
            </>
          ) : (
            <div className="flex items-center justify-center py-20">
              <p className="text-[14px] text-text-muted">{t("assessment.review.noDomains")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Approve / Reject dialogs */}
      <ApproveDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        assessmentId={id!}
        onSuccess={() => navigate("/assessments")}
      />
      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        assessmentId={id!}
        onSuccess={() => navigate("/assessments")}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => { if (!open) setDeleteOpen(false); }}
        title={t("assessment.review.deleteTitle")}
        description={t("assessment.review.deleteDesc")}
        confirmLabel={t("common.delete")}
        isDestructive
        isLoading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}
